import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, FileLock2, KeyRound, ShieldCheck, Stamp } from 'lucide-react';
import { Button } from '../ui/button';
import { gated, sentences } from '../../content/gates';
import { mockFlags } from '../mock/motion/flags';
import { trackCta } from '../../lib/analytics';
import { isPrerender } from '../../lib/prerender';
import { DealDiagram } from './DealDiagram';

// The marketing page system. One set of building blocks so every page shares
// spacing, type and tone: flat planes (white, neutral-50, navy bands), no
// gradients or ornament, hierarchy from type and spacing (dataroom CLAUDE.md).
// Content comes from src/content; gated phrases are filtered here.

const ToneContext = createContext('white');
const useTone = () => useContext(ToneContext);

const PLANES = {
  white: 'bg-white',
  grey: 'bg-neutral-50',
  navy: 'bg-primary-900',
};

export const Container = ({ className = '', children }) => (
  <div className={`mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 ${className}`}>{children}</div>
);

export const Section = ({ id, tone = 'white', className = '', children, ...rest }) => (
  <ToneContext.Provider value={tone}>
    <section
      id={id}
      className={`scroll-mt-16 py-16 lg:py-20 ${PLANES[tone]} ${tone === 'white' ? 'border-t border-neutral-200' : ''} ${className}`}
      {...rest}
    >
      <Container>{children}</Container>
    </section>
  </ToneContext.Provider>
);

// The backdrop plate behind product mocks on white and grey planes: a flat
// neutral-100 tint reaching past the mock by its own padding, so the mock
// keeps the full column, and it sits apart from the page as the navy hero
// sets mocks apart. Navy bands need none. Stacked mocks share one plate.
// ?plate=off hides it for QA comparisons.
export const AssetPlate = ({ className = '', children }) => {
  const tone = useTone();
  if (mockFlags.plate === 'off' || tone === 'navy') return <div className={className}>{children}</div>;
  return <div className={`-mx-4 bg-neutral-100 px-4 py-4 sm:-mx-6 sm:px-6 sm:py-6 lg:rounded-md ${className}`}>{children}</div>;
};

export const Eyebrow = ({ children }) => {
  const tone = useTone();
  return children ? (
    <p className={`label-uppercase mb-3 ${tone === 'navy' ? 'text-primary-300' : 'text-neutral-500'}`}>{children}</p>
  ) : null;
};

export const Heading = ({ as: Tag = 'h2', className = '', children }) => {
  const tone = useTone();
  const size =
    Tag === 'h1'
      ? 'text-4xl sm:text-5xl leading-[1.08]'
      : Tag === 'h2'
        ? 'text-3xl lg:text-[2.25rem] leading-tight'
        : 'text-lg leading-snug';
  return (
    <Tag className={`font-extrabold tracking-tight ${size} ${tone === 'navy' ? 'text-white' : 'text-neutral-900'} ${className}`}>
      {children}
    </Tag>
  );
};

export const Body = ({ className = '', children }) => {
  const tone = useTone();
  return (
    <p className={`text-base leading-relaxed lg:text-lg ${tone === 'navy' ? 'text-primary-100' : 'text-neutral-600'} ${className}`}>
      {inline(sentences(children), tone)}
    </p>
  );
};

export const SectionHeader = ({ eyebrow, title, body, className = '', as }) => (
  <div className={`max-w-3xl ${className}`}>
    <Eyebrow>{eyebrow}</Eyebrow>
    <Heading as={as}>{title}</Heading>
    {body && <Body className="mt-4">{body}</Body>}
  </div>
);

export const Bullets = ({ items, className = '' }) => {
  const tone = useTone();
  return (
    <ul className={`space-y-2.5 ${className}`}>
      {gated(items).map((item) => {
        const text = typeof item === 'string' ? item : item.text;
        return (
          <li key={text} className={`flex gap-3 text-base ${tone === 'navy' ? 'text-primary-100' : 'text-neutral-700'}`}>
            <span className={`mt-[0.6rem] h-1.5 w-1.5 flex-shrink-0 rounded-full ${tone === 'navy' ? 'bg-primary-300' : 'bg-primary-900'}`} />
            <span>{inline(text, tone)}</span>
          </li>
        );
      })}
    </ul>
  );
};

// Internal paths use the router; anything else is a plain link. Files
// (public/downloads) are plain links too, so the router doesn't take them.
const isFile = (to) => /^\/downloads\//.test(to);
const Href = ({ to, className, children, ...rest }) =>
  to.startsWith('/') && !isFile(to) ? (
    <Link to={to} className={className} {...rest}>
      {children}
    </Link>
  ) : (
    <a href={to} className={className} {...rest}>
      {children}
    </a>
  );

// Copy may carry links as [label](href) and **bold** lead-ins (the legal
// pages, the guides). Internal hrefs use the router; the link styling is the
// site's inline link, drawn white on navy bands.
const INLINE = /\*\*([^*]+)\*\*|\[([^\]]+)\]\(([^)\s]+)\)/g;
const inlineLinkClass = 'font-medium text-primary-900 underline decoration-primary-900/30 underline-offset-2 hover:decoration-primary-900';
const inlineLinkNavyClass = 'font-medium text-white underline decoration-white/40 underline-offset-2 hover:decoration-white';

export const inline = (text, tone = 'white') => {
  if (typeof text !== 'string' || !(text.includes('](') || text.includes('**'))) return text;
  const parts = [];
  let last = 0;
  for (const match of text.matchAll(INLINE)) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    if (match[1]) {
      parts.push(
        <strong key={match.index} className="font-semibold text-inherit">
          {match[1]}
        </strong>,
      );
    } else {
      parts.push(
        <Href key={match.index} to={match[3]} className={tone === 'navy' ? inlineLinkNavyClass : inlineLinkClass}>
          {match[2]}
        </Href>,
      );
    }
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
};

export const TextLink = ({ to, children, className = '' }) => {
  const tone = useTone();
  return (
    <Href
      to={to}
      onClick={() => trackCta(typeof children === 'string' ? children : to)}
      className={`inline-flex items-center gap-1.5 text-sm font-medium hover:underline underline-offset-4 ${
        tone === 'navy' ? 'text-white' : 'text-primary-900'
      } ${className}`}
    >
      {children}
      <ArrowRight className="h-4 w-4" />
    </Href>
  );
};

// Buttons: the first is primary, the rest secondary; inverse pair on navy.
export const Actions = ({ actions = [], className = '' }) => {
  const tone = useTone();
  const shown = gated(actions);
  if (!shown.length) return null;
  return (
    <div className={`flex flex-col gap-3 sm:flex-row ${className}`}>
      {shown.map((action, i) => {
        const variant =
          tone === 'navy' ? (i === 0 ? 'inverse' : 'inverse-outline') : i === 0 ? 'default' : 'secondary';
        return (
          <Button key={action.label} asChild size="lg" variant={variant} data-testid={action.testId}>
            <Href to={action.to} onClick={() => trackCta(action.label)}>
              {action.label}
            </Href>
          </Button>
        );
      })}
    </div>
  );
};

// Navy band at the top of a page. With an asset, text takes 5 columns and
// the asset 7 at xl, so the wide data-room mock gets room.
export const PageHero = ({ eyebrow, title, sub, actions, asset, testId }) => (
  <ToneContext.Provider value="navy">
    <section className="bg-primary-900 py-16 lg:py-24" data-testid={testId}>
      <Container>
        <div className={asset ? 'grid grid-cols-1 items-center gap-12 lg:grid-cols-2 xl:grid-cols-12' : 'max-w-3xl'}>
          <div className={asset ? 'xl:col-span-5' : ''}>
            <Eyebrow>{eyebrow}</Eyebrow>
            <Heading as="h1">{title}</Heading>
            {sub && <Body className="mt-6 max-w-2xl">{sub}</Body>}
            <Actions actions={actions} className="mt-9" />
          </div>
          {asset && <div className="xl:col-span-7">{asset}</div>}
        </div>
      </Container>
    </section>
  </ToneContext.Provider>
);

const STANDARD_ICONS = { sso: KeyRound, mfa: ShieldCheck, watermark: Stamp, drm: FileLock2 };

// The home page's hero: a taller navy band with the headline beside a
// diagram of the room (5/7 at xl, halves at lg), then a proof row across the
// band's foot. Security standards stand where other sites put logos.
export const HomeHero = ({ eyebrow, title, sub, actions, diagram, trustedBy, standards, testId }) => {
  const shown = gated(standards);
  const trusted = gated([trustedBy])[0];
  return (
    <ToneContext.Provider value="navy">
      <section className="bg-primary-900 pt-16 sm:pt-20 lg:pt-28" data-testid={testId}>
        <Container>
          <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-2 lg:gap-12 xl:grid-cols-12 xl:gap-16">
            <div className="xl:col-span-5">
              <Eyebrow>{eyebrow}</Eyebrow>
              <Heading as="h1" className="text-balance">
                {title}
              </Heading>
              {sub && <Body className="mt-6 max-w-xl">{sub}</Body>}
              <Actions actions={actions} className="mt-10" />
            </div>
            <div className="xl:col-span-7">
              <DealDiagram {...diagram} />
            </div>
          </div>

          <div className="mt-16 border-t border-primary-700 py-10 lg:mt-24" data-testid="hero-standards">
            {trusted && <p className="text-sm text-primary-200">{trusted.text}</p>}
            <ul className={`grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2 lg:grid-cols-4 ${trusted ? 'mt-6' : ''}`} data-qa-labels>
              {shown.map((item) => {
                const Icon = STANDARD_ICONS[item.icon];
                return (
                  <li key={item.text} className="flex items-start gap-3 text-sm font-medium text-white">
                    <Icon className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary-300" aria-hidden="true" />
                    <span>{item.text}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        </Container>
      </section>
    </ToneContext.Provider>
  );
};

// Text beside a product mock: halves at lg, 5/7 at xl so the mock gets the
// room its wide layout is drawn for. `reverse` puts the mock on the left.
// With no asset (one behind a gate that's off) the text stands alone.
export const FeatureSplit = ({ id, eyebrow, title, body, bullets, note, link, asset, reverse = false, tone = 'white', testId }) => {
  const text = (
    <>
      <SectionHeader eyebrow={eyebrow} title={title} body={body} />
      {bullets && <Bullets items={bullets} className="mt-6" />}
      {note && <p className="mt-5 text-sm text-neutral-500">{note}</p>}
      {link && <TextLink to={link.to} className="mt-6">{link.label}</TextLink>}
    </>
  );
  return (
    <Section id={id} tone={tone} data-testid={testId}>
      {asset ? (
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16 xl:grid-cols-12">
          <div className={`xl:col-span-5 ${reverse ? 'lg:order-2' : ''}`}>{text}</div>
          <div className={`xl:col-span-7 ${reverse ? 'lg:order-1' : ''}`}>
            <AssetPlate>{asset}</AssetPlate>
          </div>
        </div>
      ) : (
        <div className="max-w-3xl">{text}</div>
      )}
    </Section>
  );
};

// Term · description rows divided by rules (the app's definition-list row).
// An item's optional `link` ({ label, to }) sits on its own line under the
// text: an email address or a page to go to. With `to`, the term itself is the
// link (the guides hub).
export const TermList = ({ items, className = '' }) => (
  <dl className={`divide-y divide-neutral-200 border-y border-neutral-200 ${className}`}>
    {gated(items).map((item) => (
      <div key={item.term} className="grid grid-cols-1 gap-1 py-4 sm:grid-cols-[14rem_1fr] sm:gap-6">
        <dt className="text-base font-semibold text-neutral-900">
          {item.to ? (
            <Href to={item.to} className="text-primary-900 underline decoration-primary-900/30 underline-offset-2 hover:decoration-primary-900">
              {item.term}
            </Href>
          ) : (
            item.term
          )}
        </dt>
        <dd className="text-base text-neutral-600">
          {inline(sentences(item.text))}
          {item.link && (
            <span className="mt-1.5 block">
              <Href to={item.link.to} className={inlineLinkClass}>
                {item.link.label}
              </Href>
            </span>
          )}
        </dd>
      </div>
    ))}
  </dl>
);

// A long document (Privacy Policy, Terms of Use): numbered sections, with a
// contents list that stays in view beside them from lg. Each section is
// { id, title, body }; body blocks are a paragraph (a string, or an array of
// sentences that may carry gates), { list }, or { table: { columns, rows } }.
const DocBlock = ({ block }) => {
  if (block.h3) {
    return (
      <h3 id={block.id} className="scroll-mt-24 pt-4 text-xl font-extrabold tracking-tight text-neutral-900">
        {block.h3}
      </h3>
    );
  }
  if (block.steps) {
    return (
      <ol className="space-y-3">
        {gated(block.steps).map((item, i) => {
          const text = typeof item === 'string' ? item : item.text;
          return (
            <li key={text} className="flex gap-3 text-base leading-relaxed text-neutral-700">
              <span className="w-6 flex-shrink-0 font-semibold tabular-nums text-neutral-500">{i + 1}.</span>
              <span>{inline(text)}</span>
            </li>
          );
        })}
      </ol>
    );
  }
  if (block.list) {
    return (
      <ul className="space-y-2.5">
        {gated(block.list).map((item) => {
          const text = typeof item === 'string' ? item : item.text;
          return (
            <li key={text} className="flex gap-3 text-base leading-relaxed text-neutral-700">
              <span className="mt-[0.65rem] h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary-900" />
              <span>{inline(text)}</span>
            </li>
          );
        })}
      </ul>
    );
  }
  if (block.table) return <DataTable {...block.table} />;
  // A paragraph: a string, an array of sentences, or one gated { text }.
  const paragraph = typeof block === 'string' || Array.isArray(block) ? block : block.text;
  return <p className="text-base leading-relaxed text-neutral-700">{inline(sentences(paragraph))}</p>;
};

export const LegalDocument = ({ updated, sections, testId }) => (
  <Section data-testid={testId}>
    <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-16">
      <nav aria-label="Contents" className="lg:col-span-4 xl:col-span-3">
        <div className="lg:sticky lg:top-24">
          <p className="label-uppercase text-neutral-500">Contents</p>
          <ol className="mt-4 space-y-2">
            {sections.map((section, i) => (
              <li key={section.id} className="flex gap-2 text-sm">
                <span className="w-5 flex-shrink-0 tabular-nums text-neutral-400">{i + 1}.</span>
                <a href={`#${section.id}`} className="text-neutral-600 hover:text-primary-900">
                  {section.title}
                </a>
              </li>
            ))}
          </ol>
        </div>
      </nav>
      <article className="max-w-3xl lg:col-span-8 xl:col-span-9">
        <p className="text-sm text-neutral-500">Last updated {updated}</p>
        {sections.map((section, i) => (
          <section key={section.id} id={section.id} className="mt-8 scroll-mt-24 border-t border-neutral-200 pt-8">
            <h2 className="text-2xl font-extrabold tracking-tight text-neutral-900">
              <span className="mr-2 tabular-nums text-neutral-400">{i + 1}.</span>
              {section.title}
            </h2>
            <div className="mt-4 space-y-4">
              {gated(section.body).map((block, j) => (
                <DocBlock key={j} block={block} />
              ))}
            </div>
          </section>
        ))}
      </article>
    </div>
  </Section>
);

// Dates in the copy are ISO (2026-11-04); pages show them as "November 4, 2026".
export const formatDate = (iso) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });

// A guide's byline: author and the date it was last updated.
export const Byline = ({ author, updated, labels = { by: 'By', updated: 'Last updated' } }) => (
  <p className="text-sm text-neutral-500" data-testid="byline">
    {labels.by} {author} · {labels.updated} <time dateTime={updated}>{formatDate(updated)}</time>
  </p>
);

// A guide (an answer-first article): the contents list stays in view beside
// the sections from lg, as in LegalDocument, but sections are unnumbered and
// each opens with its answer. Sections are { id, title, answer, body }; body
// blocks are DocBlock's, plus { h3, id } and { steps }. `extra` adds sections
// rendered by the page (the questions) and `links` adds contents entries for
// sections outside the article ("How DiligensHub Helps").
export const ArticleDocument = ({ byline, sections, extra = [], links = [], contentsLabel = 'Contents', testId }) => {
  const contents = [...sections, ...extra, ...links];
  return (
    <Section data-testid={testId}>
      <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-16">
        <nav aria-label={contentsLabel} className="lg:col-span-4 xl:col-span-3">
          <div className="lg:sticky lg:top-24">
            <p className="label-uppercase text-neutral-500">{contentsLabel}</p>
            <ol className="mt-4 space-y-2">
              {contents.map((section) => (
                <li key={section.id} className="text-sm leading-snug">
                  <a href={`#${section.id}`} className="text-neutral-600 hover:text-primary-900">
                    {section.title}
                  </a>
                </li>
              ))}
            </ol>
          </div>
        </nav>
        <article className="min-w-0 max-w-3xl lg:col-span-8 xl:col-span-9">
          {byline}
          {sections.map((section) => (
            <section key={section.id} id={section.id} className="mt-8 scroll-mt-24 border-t border-neutral-200 pt-8">
              <h2 className="text-2xl font-extrabold tracking-tight text-neutral-900">{section.title}</h2>
              {section.answer && (
                <p className="mt-4 text-lg leading-relaxed text-neutral-900">{inline(sentences(section.answer))}</p>
              )}
              <div className="mt-4 space-y-4">
                {gated(section.body || []).map((block, j) => (
                  <DocBlock key={j} block={block} />
                ))}
              </div>
            </section>
          ))}
          {extra.map((section) => (
            <section key={section.id} id={section.id} className="mt-8 scroll-mt-24 border-t border-neutral-200 pt-8">
              <h2 className="text-2xl font-extrabold tracking-tight text-neutral-900">{section.title}</h2>
              <div className="mt-4">{section.content}</div>
            </section>
          ))}
        </article>
      </div>
    </Section>
  );
};

// A plain table: uppercase header, divided rows, first column emphasised.
// Cells may carry [label](href) links. `foldLast` folds the last column
// under the one before it on phones (a "Learn more" link), so a three-column
// table keeps readable columns at 343px.
// `wide` gives a many-column table a minimum width, so it scrolls inside its
// frame on phones instead of crushing its columns; `compactFirst` sizes the
// first column to its content (codes, index numbers).
export const DataTable = ({ columns, rows, foldLast = false, wide = false, compactFirst = false, className = '' }) => {
  const last = columns.length - 1;
  const phoneHidden = (i) => (foldLast && i === last ? 'hidden sm:table-cell' : '');
  return (
    <div className={`overflow-x-auto border-y border-neutral-200 ${className}`}>
      <table className={`w-full text-left ${wide ? 'min-w-[48rem]' : ''}`}>
        <thead className="bg-neutral-50">
          <tr>
            {columns.map((col, i) => (
              <th
                key={col}
                scope="col"
                className={`px-4 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-500 first:pl-0 sm:first:pl-4 ${phoneHidden(i)}`}
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-200">
          {gated(rows).map((row) => (
            <tr key={row.cells[0]} className="align-top">
              {row.cells.map((cell, i) => (
                <td
                  key={i}
                  className={`px-4 py-4 text-base first:pl-0 sm:first:pl-4 ${
                    i === 0
                      ? `font-semibold text-neutral-900 ${compactFirst ? 'w-px whitespace-nowrap' : wide ? 'w-[18%]' : 'w-[34%] sm:w-[28%]'}`
                      : 'text-neutral-600'
                  } ${phoneHidden(i)}`}
                >
                  {inline(sentences(cell))}
                  {foldLast && i === last - 1 && (
                    <span className="mt-2 block sm:hidden">{inline(sentences(row.cells[last]))}</span>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Numbered steps in a row (Home's "How it works", the solution pages).
export const StepRow = ({ steps, className = '' }) => (
  <ol className={`grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4 ${className}`}>
    {steps.map((step, i) => (
      <li key={step.title} className="border-t-2 border-primary-900 pt-4">
        <div className="text-sm font-semibold tabular-nums text-neutral-500">{String(i + 1).padStart(2, '0')}</div>
        <h3 className="mt-1 text-lg font-extrabold tracking-tight text-neutral-900">{step.title}</h3>
        <p className="mt-2 text-base text-neutral-600">{inline(sentences(step.text))}</p>
      </li>
    ))}
  </ol>
);

const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const media = window.matchMedia(query);
    const onChange = () => setMatches(media.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [query]);
  return matches;
};

const STEP_TITLE = { h2: 'text-2xl lg:text-3xl', h3: 'text-xl lg:text-2xl' };

const StepText = ({ step, label, as: Tag }) => (
  <>
    <div className="text-sm font-semibold tabular-nums text-neutral-500">{label}</div>
    <Tag className={`mt-1 text-balance font-extrabold tracking-tight text-neutral-900 ${STEP_TITLE[Tag]}`}>{step.title}</Tag>
    <p className="mt-3 max-w-xl text-base leading-relaxed text-neutral-600 lg:text-lg">{sentences(step.text)}</p>
  </>
);

// The pinned mock changes by cross-fading: the outgoing step's mock fades
// out while the new one fades in, in the same cell.
const CrossFade = ({ active, render }) => {
  const [layers, setLayers] = useState([active]);
  useEffect(() => {
    setLayers((current) => (current[current.length - 1] === active ? current : [current[current.length - 1], active]));
    const timer = setTimeout(() => setLayers([active]), 400);
    return () => clearTimeout(timer);
  }, [active]);
  return (
    <div className="grid">
      {layers.map((i, idx) => (
        <div
          key={i}
          className={`[grid-area:1/1] ${layers.length > 1 ? (idx === layers.length - 1 ? 'animate-swap-in' : 'animate-swap-out') : ''}`}
        >
          {render(i)}
        </div>
      ))}
    </div>
  );
};

// Desktop: the step text scrolls; its mock stays pinned beside it. The step
// nearest the middle of the screen is active, and only its mock is mounted,
// so it plays once as it arrives.
const StickySteps = ({ steps, assets, label, as }) => {
  const [active, setActive] = useState(0);
  const refs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(Number(entry.target.dataset.step));
        });
      },
      { rootMargin: '-45% 0px -45% 0px' },
    );
    refs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="grid grid-cols-2 gap-16">
      <ol>
        {steps.map((step, i) => (
          <li
            key={step.title}
            ref={(el) => {
              refs.current[i] = el;
            }}
            data-step={i}
            className={`flex min-h-[70vh] flex-col justify-center border-l-2 pl-8 transition-colors duration-200 ${
              i === active ? 'border-primary-900' : 'border-neutral-200'
            }`}
          >
            <StepText step={step} label={label(step, i)} as={as} />
          </li>
        ))}
      </ol>
      <div>
        <div className="sticky top-[calc(50vh-200px)]">
          <AssetPlate>
            <CrossFade active={active} render={(i) => assets[steps[i].asset]()} />
          </AssetPlate>
        </div>
      </div>
    </div>
  );
};

// Phones and tablets: each step with its mock underneath.
const StackedSteps = ({ steps, assets, label, as }) => (
  <ol className="space-y-16">
    {steps.map((step, i) => (
      <li key={step.title}>
        <StepText step={step} label={label(step, i)} as={as} />
        <AssetPlate className="mt-6">{assets[step.asset]()}</AssetPlate>
      </li>
    ))}
  </ol>
);

// A long section that shows one thing at a time, each with its mock. `steps`
// carry { title, text, asset }; `assets` maps each asset key to a render
// function; `label(step, i)` is the small line above each title; `as` is the
// titles' heading level.
export const ScrollSteps = ({ steps, assets, label, as = 'h2', className = '' }) => {
  const desktop = useMediaQuery('(min-width: 1024px)');
  const Steps = desktop ? StickySteps : StackedSteps;
  // Prerendered HTML carries both layouts, and CSS shows the one for the
  // screen's width (lg is 1024px), so nothing moves when the browser's first
  // render keeps only its own.
  if (isPrerender) {
    return (
      <div className={className}>
        <div className="hidden lg:block">
          <StickySteps steps={steps} assets={assets} label={label} as={as} />
        </div>
        <div className="lg:hidden">
          <StackedSteps steps={steps} assets={assets} label={label} as={as} />
        </div>
      </div>
    );
  }
  return (
    <div className={className}>
      <Steps steps={steps} assets={assets} label={label} as={as} />
    </div>
  );
};

// Questions and answers (a page's FAQ): each question in sentence case as a
// list term, its answer under it. Items are { q, a, gate }; an answer may be
// an array of sentences that carry gates. faqJsonLd marks up the same items.
export const FaqList = ({ items, className = '' }) => (
  <dl className={`max-w-3xl divide-y divide-neutral-200 border-y border-neutral-200 ${className}`}>
    {gated(items).map((item) => (
      <div key={item.q} className="py-6">
        <dt className="text-lg font-semibold text-neutral-900">{item.q}</dt>
        <dd className="mt-2 text-base leading-relaxed text-neutral-600">{inline(sentences(item.a))}</dd>
      </div>
    ))}
  </dl>
);

// A row of links to related pages, each with the site's arrow link.
export const RelatedLinks = ({ links, className = '' }) => (
  <ul className={`flex flex-wrap gap-x-8 gap-y-4 ${className}`}>
    {gated(links).map((link) => (
      <li key={link.to}>
        <TextLink to={link.to}>
          {link.label}
        </TextLink>
      </li>
    ))}
  </ul>
);

export const FactsStrip = ({ facts }) => (
  <dl className="grid grid-cols-2 gap-x-6 gap-y-8 border-t border-primary-700 pt-8 lg:grid-cols-4">
    {facts.map((fact) => (
      <div key={fact.label} className="flex flex-col-reverse">
        <dt className="mt-1 text-sm text-primary-200">{fact.label}</dt>
        <dd className="text-4xl font-semibold tabular-nums text-white">{fact.value}</dd>
      </div>
    ))}
  </dl>
);

export const CtaBand = ({ title, body, actions, testId }) => (
  <Section tone="grey" className="border-t border-neutral-200" data-testid={testId}>
    <div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
      <SectionHeader title={title} body={body} />
      <Actions actions={actions} className="flex-shrink-0" />
    </div>
  </Section>
);
